import type { OfferingModel } from '@sosocio/creator-types/database';
import type { ProductSettings } from '@sosocio/creator-types/project';
import {
	COLOR_FULL,
	OfferingGroups,
	PRINT_COLOR_SPECTRUM_FULL,
} from 'settings/offerings';

export { getOfferingPreviewColor as getOfferingModelPrintEffectColor } from '@sosocio/tools/svg';

/**
 * Store-independent offering-model helpers extracted from the former project
 * helper monolith: color-quantity and print-color predicates plus product-shape
 * checks.
 */

/**
 * Applies print color settings from the product settings to the offering model if applicable.
 *
 * @param offeringModel {@link OfferingModel} to potentially update with print color settings.
 * @param productSettings Optional {@link ProductSettings} containing print color preferences and original offering identifiers.
 * @param productSettings.printColors Expected print color setting (e.g., 'fc' for full color or specific color codes).
 * @param productSettings.originalGroupId Original group ID to verify against the offering model.
 * @param productSettings.originalTypeId Original type ID to verify against the offering model.
 * @returns Updated {@link OfferingModel} with print color settings applied if conditions are met;
 * otherwise, returns the original offering model unchanged.
 */
export function applyPrintColorsToOfferingModel(
	offeringModel: OfferingModel,
	productSettings?: ProductSettings,
): OfferingModel {
	const {
		originalGroupId,
		originalTypeId,
		printColors,
	} = (
		productSettings
		?? {}
	);

	if (
		typeof printColors === 'undefined'
		|| typeof originalGroupId === 'undefined'
		|| typeof originalTypeId === 'undefined'
		|| originalGroupId !== offeringModel.groupid
		|| originalTypeId !== offeringModel.typeid
	) {
		return offeringModel;
	}

	if (printColors === 'fc') {
		const fullColorSpectrum = (
			[
				offeringModel.previewColorSpectrum,
				offeringModel.printColorSpectrum,
			]
				.find((spectrum): spectrum is string => (
					typeof spectrum === 'string'
					&& PRINT_COLOR_SPECTRUM_FULL.includes(spectrum)
				))
			|| 'srgb'
		);

		return {
			...offeringModel,
			color: COLOR_FULL,
			previewColorSpectrum: fullColorSpectrum,
			printColorSpectrum: fullColorSpectrum,
		};
	}

	if (offeringModel.color <= printColors) {
		return offeringModel;
	}

	return {
		...offeringModel,
		color: printColors,
		previewColorSpectrum: `pms_${printColors}`,
		printColorSpectrum: `pms_${printColors}`,
	};
}

export function getOfferingModelColorQuantity(offeringModel: OptionalExceptFor<OfferingModel, 'previewColorSpectrum'>): number {
	if (
		!offeringModel.previewColorSpectrum
		|| PRINT_COLOR_SPECTRUM_FULL.includes(offeringModel.previewColorSpectrum)
	) {
		return COLOR_FULL;
	}

	if (offeringModel.previewColorSpectrum.startsWith('pms_')) {
		return parseInt(
			offeringModel.previewColorSpectrum.replace(
				'pms_',
				'',
			),
			10,
		);
	}

	return 1;
}

/**
 * Determines whether the offering is a logo product printed in full color.
 *
 * @param offeringModel {@link OfferingModel} context used to evaluate the print color flow.
 * @returns True when the offering is a full-color logo product.
 */
export function getOfferingModelIsFullColorLogoProduct(offeringModel?: OptionalExceptFor<OfferingModel, 'previewColorSpectrum' | 'type'> | null): boolean {
	return !!(
		offeringModel?.type === 'logo'
		&& getOfferingModelColorQuantity(offeringModel) === COLOR_FULL
	);
}

/**
 * Getter to check if this is a basic product that has multiple sides/positions to be edited
 * (for instance, front and back of a t-shirt)
 *
 * @returns {boolean} Returns true if the product is multi sided
 */
export function isMultiSidedProduct(offeringModel?: OfferingModel | null): boolean {
	if (
		offeringModel
		&& OfferingGroups(
			offeringModel.groupid,
			['BasicProducts'],
		)
	) {
		return offeringModel.minpages > 1;
	}

	return false;
}

export function shouldExcludeEmptyPages(offeringModel?: OfferingModel | null): boolean {
	/**
	 * Note: Upon migration to JSON schema this check can be performed directly on the property
	 * in the offering data, but for now we need to compile the boolean value like this
	 */
	if (
		offeringModel?.type === 'logo'
		&& offeringModel.maxpages > offeringModel.minprintpages
	) {
		return true;
	}

	return false;
}
